import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

const LoginPage = () => {

  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const apiBaseUrl = "https://installmentzone.com/pharmacy/api";

  useEffect(() => {

    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }

  }, []);

  const isTokenValid = (token) => {
    try {

      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      return decoded.exp && decoded.exp > currentTime;

    } catch (e) {
      return false;
    }
  };

  const handleLogin = async () => {

    setError("");

    if (!email || !password) {
      setError("Please fill in both email and password.");
      return;
    }

    setLoading(true);

    try {

      // LOGIN API
      const response = await axios.post(`${apiBaseUrl}/login_user.php`, {
        email,
        password
      });

      if (response.data.status === "success") {

        const token = response.data.token;

        if (!isTokenValid(token)) {
          setError("Session expired. Please login again.");
          return;
        }

        localStorage.setItem("token", token);

        // Remember Me
        if (rememberMe) {
          localStorage.setItem("rememberEmail", email);
          localStorage.setItem("rememberPassword", password);
        } else {
          localStorage.removeItem("rememberEmail");
          localStorage.removeItem("rememberPassword");
        }

        // CHECK LICENSE
        try {

          const licenseRes = await axios.get(
            `${apiBaseUrl}/check_certificate.php`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          console.log("License API response:", licenseRes.data);

          if (licenseRes.data.status !== "success") {

            if (licenseRes.data.message === "License expired. Please renew.") {

              navigate("/renew-license");
              return;

            }

            setError(licenseRes.data.message);
            return;

          }

        } catch (err) {

          console.error("License check failed:", err.response?.data || err);

          if (err.response && err.response.data && err.response.data.message) {

            // LICENSE EXPIRED
            if (err.response.data.message === "License expired. Please renew.") {

              navigate("/renew-license");
              return;

            }

            setError(err.response.data.message);

          } else {

            setError("Server error while checking license.");

          }

          return;

        }

        // LOGIN SUCCESS
        alert("Login successful!");

        navigate("/dashboard");

      } else {

        setError(response.data.message);

      }

    } catch (err) {

      console.error(err.response?.data || err);

      setError("Login failed. Please try again.");

    } finally {

      setLoading(false);

    }

  };

  return (

    <div className="flex justify-center items-center min-h-screen bg-gray-100">

      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">

        <h1 className="text-3xl font-bold mb-6 text-center text-blue-700">
          Pharmacy Login
        </h1>

        {error && (
          <p className="text-red-600 mb-5 text-center font-medium">{error}</p>
        )}

        <div className="mb-5">
          <label className="block mb-2 font-medium text-gray-700">Email</label>

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email"
          />

        </div>

        <div className="mb-6 relative">

          <label className="block mb-2 font-medium text-gray-700">
            Password
          </label>

          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your password"
          />

          <span
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-[42px] text-gray-500 cursor-pointer"
          >
            {showPassword ? "Hide" : "Show"}
          </span>

        </div>

        <div className="mb-6 flex items-center">

          <input
            type="checkbox"
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
            className="mr-2"
          />

          <label className="text-gray-700 cursor-pointer">
            Remember me
          </label>

        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          className={`w-full py-3 rounded-lg text-white font-semibold ${
            loading
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        <div className="mt-6 text-center text-gray-600">

          Don't have an account?{" "}

          <Link
            to="/register"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Register now
          </Link>

        </div>

      </div>

    </div>

  );

};

export default LoginPage;